import AppData from "../app.data";
import { cleanupAnimationEntry, getAnimationMixer } from "./runtime-registry.utils";
import { getVRModelOriginalMaterial } from "./vrModelPerformance.utils";

const MODEL_MIXER_USER_DATA_KEY = "__artetraAnimationMixers";

/**
 * Recursively disposes of Three.js objects, materials, and textures
 * Cleans up memory by properly disposing of all resources
 * @param obj Object to dispose
 * @param selfKeep Optional flag to keep the object itself while disposing children
 * @returns Boolean indicating successful disposal
 */
export function disposeOfChildren(obj, selfKeep?) {
    if (!obj) return false;

    const appData = new AppData();
    const materials = new Set();
    const mixers = new Set<any>();

    // First pass: traverse objects, dispose geometries, and collect materials
    obj.traverse((child) => {
        const childMixers = child?.userData?.[MODEL_MIXER_USER_DATA_KEY];
        if (Array.isArray(childMixers)) {
            childMixers.forEach((mixer) => mixers.add(mixer));
        }

        if (child.skeleton?.dispose) {
            child.skeleton.dispose();
        }

        if (child.geometry) {
            child.geometry.dispose();
            child.geometry = undefined;
        }

        const childMaterials = Array.isArray(child.material)
            ? child.material
            : child.material
                ? [child.material]
                : [];

        childMaterials.forEach((mat) => materials.add(mat));
        const originalVRMaterial = getVRModelOriginalMaterial(child);
        const originalVRMaterials = Array.isArray(originalVRMaterial)
            ? originalVRMaterial
            : originalVRMaterial
                ? [originalVRMaterial]
                : [];
        originalVRMaterials.forEach((mat) => materials.add(mat));

        // Break object->material references to help GC
        if (childMaterials.length) {
            child.material = undefined;
        }
    });

    // Dispose every texture-like value on the material, then the material itself
    const disposeMaterial = (material) => {
        if (!material) return;

        const disposeTextureValue = (value) => {
            if (value?.isTexture || value?.isWebGLRenderTarget) {
                value.dispose();
                return true;
            }
            return false;
        };

        Object.keys(material).forEach((key) => {
            const value = material[key];

            if (disposeTextureValue(value)) {
                material[key] = null;
                return;
            }

            if (Array.isArray(value) && value.length && value[0]?.isTexture) {
                value.forEach((tex) => tex?.dispose());
                material[key] = null;
            }
        });

        // Handle shader uniforms that may contain textures
        if (material.uniforms) {
            Object.values(material.uniforms).forEach((uniform: any) => {
                const uniformValue = uniform?.value;
                if (disposeTextureValue(uniformValue)) {
                    uniform.value = null;
                } else if (Array.isArray(uniformValue) && uniformValue[0]?.isTexture) {
                    uniformValue.forEach((tex) => tex?.dispose());
                    uniform.value = null;
                }
            });
        }

        material.dispose();
    };

    materials.forEach(disposeMaterial);
    if (mixers.size && Array.isArray(appData.skinnedMeshes)) {
        mixers.forEach((mixer) => {
            cleanupAnimationEntry(mixer);
            const index = appData.skinnedMeshes.findIndex((entry) => getAnimationMixer(entry) === mixer);
            if (index >= 0) {
                appData.skinnedMeshes.splice(index, 1);
            }
        });
    }

    // Remove children; optionally detach the root
    obj.clear();
    if (!selfKeep && obj.parent) {
        obj.removeFromParent();
    }

    return true;
}

